import {
  BoundMultiSelectField,
  BoundSelectField,
  BoundSwitchField,
  Button,
  Css,
  FormLines,
  IconButton,
  StaticField,
  Switch,
  Tooltip,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { BidScopeSource, CostType } from "src/generated/graphqlTypes";
import { AddBidPackagePage_OriginatingProjectFragment } from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import {
  BidPackageDetailsStepQuery,
  useBidPackageDetailsStepQuery,
} from "src/routes/bids/add-bid-package/components/1.BidPackageDetailsStep.generated";
import { isDefined, noop, queryResult } from "src/utils";
import { BidPackageFormState } from "../AddBidPackagePage";
import { BidScopeFields } from "./BidScopeFields";

type BidPackageDetailsStepProps = {
  formState: ObjectState<BidPackageFormState>;
  originatingProject?: AddBidPackagePage_OriginatingProjectFragment;
};

export function BidPackageDetailsStep({ formState, originatingProject }: BidPackageDetailsStepProps) {
  const query = useBidPackageDetailsStepQuery();
  return queryResult(query, (data) => (
    <BidPackageDetailsView {...data} formState={formState} originatingProject={originatingProject} />
  ));
}

function BidPackageDetailsView({
  formState,
  costCodes,
  markets,
  bidScopeSourceDetail,
  originatingProject,
}: BidPackageDetailsStepQuery & BidPackageDetailsStepProps) {
  return (
    <>
      <div css={Css.df.fdc.aic.$}>
        <div css={Css.df.fdc.aic.my6.$}>
          <div css={Css.xl2.$}>Create a bid package</div>
          <div css={Css.md.pt2.$}>Get started by bidding out as many communities and cost codes as you need to.</div>
        </div>

        <div css={Css.w50.p4.bgWhite.bshBasic.br12.mb3.$}>
          <div css={Css.lg.mb2.$}>Create Package Overview</div>
          <div css={Css.sm.mb3.$}>
            The cost codes and product offerings here will determine which lines of scope are auto filled into your
            packages for bidding.
          </div>
          <FormLines labelStyle="left" width="full">
            <BidScopeFields
              formState={formState}
              markets={markets}
              scopeSources={bidScopeSourceDetail}
              originatingProject={originatingProject}
            />
            <div css={Css.df.fdr.aic.$}>
              <span css={Css.w50.gray700.$}>Is Unit Based Bid</span>
              <Observer>
                {() =>
                  // Project level bid packages must be unit based
                  isDefined(formState.scopeSource.value) &&
                  formState.scopeSource.value !== BidScopeSource.ProductOffering ? (
                    // disabled tooltips on switches are associated to the label which is hidden so we need to manually add it
                    <Tooltip title="Project Level bid packages must be unit based." placement="top">
                      <Switch
                        selected
                        onChange={noop}
                        label="Is Unit Based Bid"
                        labelStyle="hidden"
                        disabled="Project Level bid packages must be unit based."
                      />
                    </Tooltip>
                  ) : (
                    <BoundSwitchField
                      field={formState.isUnitBased}
                      onChange={(val) => formState.isUnitBased.set(val)}
                      label="Is Unit Based Bid"
                      labelStyle="hidden"
                    />
                  )
                }
              </Observer>
            </div>
            <Observer>
              {() => (
                <StaticField label="Cost Codes *">
                  <div css={Css.w100.df.fdc.gap1.$}>
                    {formState.bidPackages.rows.compact().map((bp, i) => {
                      // Filter out cost codes that are in other bid packages
                      const availableCostCodes = costCodes.filter(
                        (cc) =>
                          !formState.bidPackages.rows.some((bp, j) => j !== i && bp.costCodes.value?.includes(cc.id)),
                      );
                      // Check if this bid package should be deleted (removing from the formstate does not delete in the DB)
                      if (bp.delete.value === true) return null;
                      return (
                        <div css={Css.df.gapPx(4).$} key={i}>
                          <BoundMultiSelectField
                            field={bp.costCodes}
                            options={availableCostCodes}
                            labelStyle="hidden"
                            multiline
                          />

                          {formState.bidPackages.rows.length > 1 && (
                            <div css={Css.ptPx(2).$}>
                              <IconButton
                                icon="trash"
                                // we need to remove the bid package from the DB using this boolean, otherwise it will only be removed from the UI
                                onClick={() => {
                                  if (isDefined(bp.id.value)) {
                                    bp.delete.set(true);
                                  } else {
                                    formState.bidPackages.remove(i);
                                  }
                                }}
                                label="Remove bid package"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {!formState.isInternalEstimate.value && (
                      <div css={Css.mb2.$}>
                        <Button
                          variant="text"
                          icon="plus"
                          label="Add another bid package"
                          onClick={() => formState.bidPackages.add({})}
                        />
                      </div>
                    )}
                  </div>
                </StaticField>
              )}
            </Observer>
            <BoundSelectField
              field={formState.costType}
              getOptionValue={(ct) => ct.code}
              getOptionLabel={(ct) => ct.name}
              options={[
                { code: CostType.Labor, name: "Labor" },
                { code: CostType.Materials, name: "Material" },
                { code: CostType.Subcontractor, name: "Subcontractor" },
              ]}
            />
          </FormLines>
        </div>
      </div>
    </>
  );
}
